import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Matches the shape of PrimeNG's Confirmation interface closely enough
 * that existing calls like confirmSvc.confirm({ header, message, ... })
 * continue to work unchanged.
 */
export interface AppConfirmation {
  header?: string;
  message: string;
  acceptLabel?: string;
  rejectLabel?: string;
  acceptButtonStyleClass?: string;
  rejectButtonStyleClass?: string;
  accept?: () => void;
  reject?: () => void;
}

/**
 * Drop-in replacement for PrimeNG's ConfirmationService.
 *
 * MIGRATION: in every file that currently does
 *   import { ConfirmationService } from 'primeng/api';
 *   constructor(private confirmSvc: ConfirmationService) {}
 *
 * change the import to:
 *   import { AppConfirmationService } from '<path>';
 *   constructor(private confirmSvc: AppConfirmationService) {}
 *
 * The variable name `confirmSvc` and the `.confirm({...})` call stay
 * exactly the same — only the imported type name changes.
 */
@Injectable({ providedIn: 'root' })
export class AppConfirmationService {
  private readonly activeConfirmationSubject = new BehaviorSubject<AppConfirmation | null>(null);
  /** Subscribed to by AppConfirmDialogComponent to render the active confirmation */
  activeConfirmation$ = this.activeConfirmationSubject.asObservable();

  /** Matches PrimeNG's `confirm(confirmation: Confirmation)` signature */
  confirm(confirmation: AppConfirmation): void {
    this.activeConfirmationSubject.next(confirmation);
  }

  /** Called internally by AppConfirmDialogComponent when Accept is clicked */
  onAccept(): void {
    const current = this.activeConfirmationSubject.value;
    current?.accept?.();
    this.close();
  }

  /** Called internally by AppConfirmDialogComponent when Reject/Cancel is clicked, or the dialog is dismissed */
  onReject(): void {
    const current = this.activeConfirmationSubject.value;
    current?.reject?.();
    this.close();
  }

  private close(): void {
    this.activeConfirmationSubject.next(null);
  }
}